import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, FileText, Image, File, Pause, Play, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useJobFiles } from '@/hooks/useJobFiles';
import { FileUploadData, VISIBILITY_OPTIONS, FILE_CATEGORIES, MAX_FILE_SIZE } from '@/lib/types/file';
import { MilestoneCompletionReminderDialog } from './MilestoneCompletionReminderDialog';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';
import { formatBytes, formatTime, formatSpeed } from '@/lib/upload/formatters';
import { TUS_THRESHOLD } from '@/lib/upload/constants';
import { isUploadAbortedError } from '@/lib/upload';

// All file uploads should trigger milestone completion reminder

interface JobFileUploadProps {
  jobId: string;
  userRole: 'client' | 'vendor';
  onUploadComplete?: () => void;
  multiple?: boolean;
  className?: string;
}

interface UploadingFile extends FileUploadData {
  progress: number;
  error?: string;
  isUploading?: boolean;
}

export const JobFileUpload: React.FC<JobFileUploadProps> = ({
  jobId,
  userRole,
  onUploadComplete,
  multiple = true,
  className = ''
}) => {
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [uploadSettings, setUploadSettings] = useState<{
    visibility_level: FileUploadData['visibility_level'];
    file_category: FileUploadData['file_category'] | '';
    description: string;
  }>({
    visibility_level: userRole === 'client' ? 'award_only' : 'client_visible',
    file_category: '',
    description: ''
  });
  const [showMilestoneReminderDialog, setShowMilestoneReminderDialog] = useState(false);
  const [uploadedCategory, setUploadedCategory] = useState<string>('');
  const [isPaused, setIsPaused] = useState(false);
  
  const { uploadFile, validateFile, uploadProgress, cancelUpload, pauseUpload, resumeUpload } = useJobFiles({ jobId });
  const { toast } = useToast();

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setStagedFiles(prev => [...prev, ...acceptedFiles]);
  }, []);

  const handleUpload = async () => {
    if (stagedFiles.length === 0) return;
    if (!uploadSettings.file_category) {
      toast({
        title: "Category required",
        description: "Please select a file category before uploading.",
        variant: "destructive",
      });
    return;
  }

  setIsPaused(false);  // Reset pause state for new uploads

  const filesToUpload: UploadingFile[] = stagedFiles.map(file => ({
      file,
      visibility_level: uploadSettings.visibility_level,
      file_category: uploadSettings.file_category as FileUploadData['file_category'],
      description: uploadSettings.description,
      progress: 0
    }));

    setUploadingFiles(filesToUpload);
    setStagedFiles([]);

    for (let i = 0; i < filesToUpload.length; i++) {
      const fileUpload = filesToUpload[i];
      
      try {
        // Validate file
        const validationError = validateFile(fileUpload.file);
        if (validationError) {
          throw new Error(validationError);
        }

        // Mark as currently uploading
        setUploadingFiles(prev => prev.map((f, index) => 
          index === i ? { ...f, isUploading: true } : f
        ));

        await uploadFile({
          file: fileUpload.file,
          visibility_level: fileUpload.visibility_level,
          file_category: fileUpload.file_category,
          description: fileUpload.description
        });
        
        setUploadingFiles(prev => prev.map((f, index) => 
          index === i ? { ...f, progress: 100, isUploading: false } : f
        ));

        // Track file upload
        analytics.track({
          name: ANALYTICS_EVENTS.FILE_UPLOADED,
          category: 'engagement',
          properties: {
            job_id: jobId,
            file_category: fileUpload.file_category,
            file_type: fileUpload.file.type,
          },
        });

        toast({
          title: "File uploaded successfully",
          description: fileUpload.file.name,
        });
      } catch (error) {
        // Check if this was an intentional cancellation - skip error handling
        if (isUploadAbortedError(error)) {
          setUploadingFiles(prev => prev.filter((_, idx) => idx !== i));
          return;
        }
        
        setUploadingFiles(prev => prev.map((f, index) => 
          index === i ? { ...f, error: error instanceof Error ? error.message : 'Upload failed', isUploading: false } : f
        ));
        
        toast({
          title: "Upload failed",
          description: error instanceof Error ? error.message : 'Failed to upload file',
          variant: "destructive",
        });
      }
    }

    // All file uploads should trigger milestone reminder
    const shouldShowMilestoneReminder = uploadSettings.file_category !== '';
    const categoryForReminder = uploadSettings.file_category;
    
    // Clear completed uploads after a delay
    setTimeout(() => {
      setUploadingFiles([]);
      onUploadComplete?.();
      
      // Show milestone reminder dialog for relevant categories
      if (shouldShowMilestoneReminder) {
        setUploadedCategory(categoryForReminder);
        setShowMilestoneReminderDialog(true);
      }
    }, 2000);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/zip': ['.zip'],
      'text/plain': ['.txt']
    },
    maxSize: MAX_FILE_SIZE
  });

  const removeUploadingFile = (index: number) => {
    const file = uploadingFiles[index];
    if (file?.isUploading) {
      cancelUpload();
    }
    setUploadingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handlePauseResume = () => {
    if (isPaused) {
      resumeUpload();
      setIsPaused(false);
    } else {
      pauseUpload();
      setIsPaused(true);
    }
  };

  const handleCancelUpload = (index: number) => {
    cancelUpload();
    setUploadingFiles(prev => prev.filter((_, i) => i !== index));
    setIsPaused(false);
  };

  const removeStagedFile = (index: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const isLargeFile = (file: File) => file.size >= TUS_THRESHOLD;

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return <Image className="h-4 w-4" />;
    if (file.type === 'application/pdf') return <FileText className="h-4 w-4" />;
    return <File className="h-4 w-4" />;
  };

  const visibilityOptions = VISIBILITY_OPTIONS[userRole];

  return (
    <div className={className}>
      {/* Upload Settings Section - Inline */}
      <div className="space-y-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold">Upload Settings</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Configure how your files will be uploaded. These settings apply to all files you upload.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Visibility Setting */}
          <div className="space-y-2">
            <Label htmlFor="visibility">Visibility</Label>
            <Select 
              value={uploadSettings.visibility_level} 
              onValueChange={(value: FileUploadData['visibility_level']) =>
                setUploadSettings(prev => ({ ...prev, visibility_level: value }))
              }
            >
              <SelectTrigger id="visibility">
                <SelectValue placeholder="Select visibility" />
              </SelectTrigger>
              <SelectContent>
                {visibilityOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    <div>
                      <div className="font-medium">{option.label}</div>
                      <div className="text-sm text-muted-foreground">{option.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Category Setting */}
          <div className="space-y-2">
            <Label htmlFor="category">Category <span className="text-destructive">*</span></Label>
            <Select 
              value={uploadSettings.file_category || undefined} 
              onValueChange={(value: FileUploadData['file_category']) =>
                setUploadSettings(prev => ({ ...prev, file_category: value }))
              }
            >
              <SelectTrigger id="category" className={!uploadSettings.file_category ? 'text-muted-foreground' : ''}>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {FILE_CATEGORIES.map(category => (
                  <SelectItem key={category.value} value={category.value}>
                    {category.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Description Setting - Full Width */}
        <div className="space-y-2">
          <Label htmlFor="description">Description (Optional)</Label>
          <Textarea
            id="description"
            placeholder="Describe this file..."
            value={uploadSettings.description}
            onChange={(e) => setUploadSettings(prev => ({ ...prev, description: e.target.value }))}
            rows={2}
          />
        </div>
      </div>

      {/* Drag & Drop Area */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${isDragActive 
            ? 'border-primary bg-primary/5' 
            : 'border-muted-foreground/25 hover:border-primary/50'
          }`}
      >
        <input {...getInputProps()} />
        
        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        
        {isDragActive ? (
          <p className="text-primary">Drop files here...</p>
        ) : (
          <div>
            <p className="text-foreground mb-1">
              Drag & drop files here, or click to select
            </p>
            <p className="text-sm text-muted-foreground">
              Max 200MB • PDF, JPG, PNG, DOCX, CSV, XLSX, ZIP, TXT
            </p>
          </div>
        )}
      </div>

      {/* Staged Files */}
      {stagedFiles.length > 0 && (
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Files Ready to Upload ({stagedFiles.length})</h4>
            <Button 
              onClick={handleUpload}
              disabled={uploadingFiles.length > 0}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload {stagedFiles.length} {stagedFiles.length === 1 ? 'File' : 'Files'}
            </Button>
          </div>
          <div className="space-y-2">
            {stagedFiles.map((file, index) => (
              <div key={index} className="border rounded-lg p-3 bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getFileIcon(file)}
                    <div className="flex-1">
                      <span className="text-sm font-medium truncate block">
                        {file.name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeStagedFile(index)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Uploading Files */}
      {uploadingFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="font-medium">Uploading...</h4>
          {uploadingFiles.map((fileUpload, index) => (
            <div key={index} className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {getFileIcon(fileUpload.file)}
                  <div className="flex-1">
                    <span className="text-sm font-medium truncate block">
                      {fileUpload.file.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {visibilityOptions.find(v => v.value === fileUpload.visibility_level)?.label}
                      {isLargeFile(fileUpload.file) && ' • Resumable upload'}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  {/* Pause/Resume button for large files */}
                  {fileUpload.isUploading && isLargeFile(fileUpload.file) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handlePauseResume}
                      className="h-6 w-6 p-0"
                      title={isPaused ? 'Resume upload' : 'Pause upload'}
                    >
                      {isPaused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
                    </Button>
                  )}
                  
                  {/* Cancel button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fileUpload.isUploading ? handleCancelUpload(index) : removeUploadingFile(index)}
                    className="h-6 w-6 p-0"
                    title="Cancel upload"
                  >
                    {fileUpload.isUploading ? <Square className="h-3 w-3" /> : <X className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
              
              {fileUpload.error ? (
                <p className="text-sm text-destructive">{fileUpload.error}</p>
              ) : fileUpload.isUploading && uploadProgress ? (
                <div className="space-y-1">
                  <Progress value={uploadProgress.percentage} className="h-2" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>
                      {formatBytes(uploadProgress.bytesUploaded)} / {formatBytes(uploadProgress.bytesTotal)}
                    </span>
                    <div className="flex gap-2">
                      {uploadProgress.uploadSpeed && (
                        <span>{formatSpeed(uploadProgress.uploadSpeed)}</span>
                      )}
                      {uploadProgress.estimatedTimeRemaining && (
                        <span>~{formatTime(uploadProgress.estimatedTimeRemaining)} remaining</span>
                      )}
                    </div>
                  </div>
                  {isPaused && (
                    <p className="text-xs text-warning font-medium">Paused</p>
                  )}
                </div>
              ) : fileUpload.progress === 100 ? (
                <div className="space-y-1">
                  <Progress value={100} className="h-2" />
                  <p className="text-xs text-primary font-medium">Upload complete</p>
                </div>
              ) : (
                <div className="space-y-1">
                  <Progress value={0} className="h-2" />
                  <p className="text-xs text-muted-foreground">Preparing...</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <MilestoneCompletionReminderDialog
        open={showMilestoneReminderDialog}
        onOpenChange={setShowMilestoneReminderDialog}
        jobId={jobId}
        userRole={userRole}
        uploadedCategory={uploadedCategory}
        onComplete={onUploadComplete}
      />
    </div>
  );
};